import React, { useMemo } from 'react';
import { Table, TableColumnConfig } from '@weave/design-system';
import { EntityTagEvent } from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import {
  getEventTime,
  getEventTypeMeta,
  getReasonMeta,
  sortEventsChronologically,
  summarizeHistory,
} from './entityTagHistory.utils';
import {
  contentClasses,
  enumCellClasses,
  sectionClasses,
  sharedClasses,
} from './EntityTagHistory.styles';
import {
  ActorLabel,
  EventTimestamp,
  Pill,
  TagStateLabel,
} from './EntityTagHistoryParts.component';
import {
  EntityTagHistorySummary,
  EntityTagHistoryTimeline,
} from './EntityTagHistoryTimeline.component';
import type {
  EntityTagHistoryContentProps,
  EnumCellProps,
  SectionProps,
} from './entityTagHistory.types';

const notProvided = <span className={sharedClasses.mutedText}>Not provided</span>;

const EnumCell = ({ meta, raw }: EnumCellProps) =>
  raw ? (
    <span className={enumCellClasses.root}>
      <Pill tone={meta.tone} direction={meta.direction} title={meta.description}>
        {meta.label}
      </Pill>
      <span className={enumCellClasses.rawValue}>{raw}</span>
    </span>
  ) : (
    notProvided
  );

export function EntityTagHistoryContent({
  events,
  actorNames,
  isComplete = true,
  showVisualization = true,
}: EntityTagHistoryContentProps) {
  const now = useMemo(() => Date.now(), [events]);
  const timedEvents = useMemo(() => sortEventsChronologically(events), [events]);
  const summary = useMemo(() => summarizeHistory(timedEvents), [timedEvents]);
  const newestFirst = useMemo(
    () =>
      [...events].sort((a, b) => {
        const aTime = getEventTime(a);
        const bTime = getEventTime(b);
        if (Number.isNaN(aTime)) return 1;
        if (Number.isNaN(bTime)) return -1;
        return bTime - aTime;
      }),
    [events]
  );

  const columns: TableColumnConfig<EntityTagEvent>[] = [
    {
      Header: 'Event',
      id: 'eventType',
      accessor: 'eventType',
      cellRenderer: (value: EntityTagEvent['eventType']) => (
        <EnumCell meta={getEventTypeMeta(value)} raw={value} />
      ),
      width: 230,
    },
    {
      Header: 'Set time',
      id: 'setTime',
      accessor: 'setTime',
      cellRenderer: (value: EntityTagEvent['setTime']) => (
        <EventTimestamp value={value} />
      ),
      width: 170,
    },
    {
      Header: 'Actor',
      id: 'actorId',
      accessor: 'actorId',
      cellRenderer: (value: EntityTagEvent['actorId']) => (
        <ActorLabel actorId={value} actorNames={actorNames} />
      ),
      width: 260,
    },
    {
      Header: 'Exists',
      id: 'tagExists',
      accessor: 'tagExists',
      cellRenderer: (value: EntityTagEvent['tagExists']) => (
        <TagStateLabel tagged={value} />
      ),
      width: 140,
    },
    {
      Header: 'Reason',
      id: 'tagExistsReason',
      accessor: 'tagExistsReason',
      cellRenderer: (value: EntityTagEvent['tagExistsReason']) => (
        <EnumCell meta={getReasonMeta(value)} raw={value} />
      ),
      width: 230,
    },
    {
      Header: 'Material change',
      id: 'materialChange',
      accessor: 'materialChange',
      cellRenderer: (value: EntityTagEvent['materialChange']) =>
        value === undefined ? (
          notProvided
        ) : value ? (
          <Pill
            tone="neutral"
            title="The API flagged this event as a material change (materialChange: true)."
          >
            Yes
          </Pill>
        ) : (
          <span className={sharedClasses.mutedText}>No</span>
        ),
      width: 140,
    },
  ];

  if (!events.length) {
    return (
      <div className={contentClasses.emptyState}>No tag history found for this view.</div>
    );
  }

  return (
    <div className={contentClasses.root}>
      {showVisualization && timedEvents.length > 0 && (
        <>
          <EntityTagHistorySummary
            summary={summary}
            latestEvent={timedEvents[timedEvents.length - 1]}
            actorNames={actorNames}
            isComplete={isComplete}
          />
          <Section
            title="State timeline"
            description="How the tag check result for this entity changed over time. Hover or focus a marker for details."
          >
            <EntityTagHistoryTimeline
              events={timedEvents}
              actorNames={actorNames}
              isComplete={isComplete}
              now={now}
            />
          </Section>
        </>
      )}
      <Section
        title="Event log"
        description={`${events.length} event${
          events.length === 1 ? '' : 's'
        }, newest first`}
      >
        <div className={contentClasses.tableScroller}>
          <Table
            data={newestFirst}
            colConfig={columns}
            isPaginated={true}
            tableInstanceId="entity-tag-history"
          />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, description, children }: SectionProps) {
  return (
    <section className={sectionClasses.root}>
      <header className={sectionClasses.header}>
        <h3 className={sectionClasses.title}>{title}</h3>
        <p className={sectionClasses.description}>{description}</p>
      </header>
      {children}
    </section>
  );
}
